// UptimeChart — 7-day uptime bar chart
// FR-012: Percentage labels on bars for color-blind accessibility
// FR-013: Skeleton/shimmer loading placeholder
// FR-014: Inline error with retry
import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

interface UptimeMetric {
  timestamp: string;
  statusCode: number;
}

interface Props {
  metrics: UptimeMetric[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

function getColor(pct: number | null): string {
  if (pct === null) return '#475569';   // gray — no data
  if (pct >= 99) return '#10b981';      // status-up
  if (pct >= 95) return '#f59e0b';      // status-degraded
  return '#ef4444';                      // status-down
}

/** FR-012: Render percentage label above each bar */
function renderBarLabel(props: any) {
  const { x, y, width, value } = props;
  if (value === null || value === undefined) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      fill="#94a3b8"
      textAnchor="middle"
      fontSize={11}
      aria-label={`${value}%`}
    >
      {`${value}%`}
    </text>
  );
}

/** FR-013: Skeleton placeholder matching bar chart dimensions */
function UptimeChartSkeleton() {
  return (
    <div className="bg-surface rounded-lg p-4 border border-border">
      <div className="h-5 w-32 bg-surface-hover rounded animate-pulse mb-3" />
      <div className="flex items-end gap-2 h-[200px] pt-4 pb-6 px-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
            <div
              className="w-full max-w-[40px] bg-surface-hover rounded-t animate-pulse"
              style={{ height: `${40 + Math.random() * 50}%` }}
            />
            <div className="h-3 w-8 bg-surface-hover rounded animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function UptimeChart({ metrics, loading, error, onRetry }: Props) {
  // FR-013: Show skeleton while loading
  if (loading) {
    return <UptimeChartSkeleton />;
  }

  // FR-014: Show inline error with retry
  if (error) {
    return (
      <div className="bg-surface rounded-lg p-4 border border-border">
        <h3 className="text-text-primary font-semibold mb-3">7-Day Uptime</h3>
        <div className="flex flex-col items-center justify-center h-[200px] text-center">
          <p className="text-text-muted text-sm mb-2">Failed to load uptime data</p>
          <p className="text-text-muted text-xs mb-3">{error}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-brand text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-brand rounded px-2 py-1"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  const data = useMemo(() => {
    const now = new Date();
    const days: { label: string; date: string; total: number; up: number }[] = [];

    for (let i = 6; i >= 0; i--) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i));
      const dateStr = d.toISOString().slice(0, 10);
      days.push({
        label: d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
        date: dateStr,
        total: 0,
        up: 0,
      });
    }

    for (const m of metrics) {
      const dateStr = new Date(m.timestamp).toISOString().slice(0, 10);
      const day = days.find((d) => d.date === dateStr);
      if (day) {
        day.total++;
        if (m.statusCode >= 200 && m.statusCode < 400) day.up++;
      }
    }

    return days.map((d) => ({
      name: d.label,
      uptime: d.total > 0 ? Math.round((d.up / d.total) * 10000) / 100 : null,
      checks: d.total,
    }));
  }, [metrics]);

  return (
    <div className="bg-surface rounded-lg p-4 border border-border">
      <h3 className="text-text-primary font-semibold mb-3">7-Day Uptime</h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 20, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} />
          <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
          <Tooltip
            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
            labelStyle={{ color: '#f8fafc' }}
            formatter={(value: any, _name: string, props: any) => {
              if (value === null) return ['No data', 'Uptime'];
              return [`${value}% (${props.payload.checks} checks)`, 'Uptime'];
            }}
          />
          <Bar dataKey="uptime" radius={[4, 4, 0, 0]} maxBarSize={40}>
            {data.map((entry, index) => (
              <Cell key={index} fill={getColor(entry.uptime)} />
            ))}
            {/* FR-012: Percentage labels above bars for accessibility */}
            <LabelList dataKey="uptime" content={renderBarLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
